import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useCreateChapter } from "@/api/chapters";
import { useState, useCallback } from "react";
import { X, KeyRound, Mail, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import PropTypes from "prop-types";

const formSchema = z.object({
  chapterName: z.string().min(2, { message: "Chapter name is required" }),
  targetCount: z.string().optional(),
  costPerStudentINR: z.string().optional(),
  costPerStudentForeign: z.string().optional(),
});

export function CreateChapterForm({ onSuccess }) {
  const [mahalluList, setMahalluList] = useState([]);
  const [bulkText, setBulkText] = useState("");

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      chapterName: "",
      targetCount: "",
      costPerStudentINR: "500",
      costPerStudentForeign: "25",
    },
  });

  const { mutate: createChapter, isPending } = useCreateChapter();

  const chapterName = form.watch("chapterName");
  const targetCount = form.watch("targetCount");
  const slug = chapterName
    ? chapterName
        .toLowerCase()
        .replace(/\s+/g, "")
        .replace(/[^a-z0-9]/g, "")
    : "";
  const autoEmail = slug ? `${slug}hadia@gmail.com` : "";
  const autoPassword = slug ? `${slug}${targetCount || "0"}` : "";

  const handleBulkAdd = useCallback(() => {
    const lines = bulkText
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    const unique = [...new Set([...mahalluList, ...lines])];
    setMahalluList(unique);
    setBulkText("");
  }, [bulkText, mahalluList]);

  const removeMahallu = (name) => {
    setMahalluList(mahalluList.filter((m) => m !== name));
  };

  function onSubmit(values) {
    const submitData = {
      ...values,
      targetCount: values.targetCount ? parseInt(values.targetCount, 10) : 0,
      targetConfig: {
        costPerStudentINR: values.costPerStudentINR
          ? parseInt(values.costPerStudentINR, 10)
          : 500,
        costPerStudentForeign: values.costPerStudentForeign
          ? parseInt(values.costPerStudentForeign, 10)
          : 25,
      },
      mahalluList,
    };
    delete submitData.costPerStudentINR;
    delete submitData.costPerStudentForeign;
    createChapter(submitData, {
      onSuccess: () => {
        form.reset();
        setMahalluList([]);
        setBulkText("");
        onSuccess?.();
      },
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        <FormField
          control={form.control}
          name="chapterName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Chapter Name</FormLabel>
              <FormControl>
                <Input placeholder="e.g. ABU DHABI" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="targetCount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Target Sponsorship Count</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder="Number of students to sponsor"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Target Configuration */}
        <div className="grid grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="costPerStudentINR"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cost/Student (INR)</FormLabel>
                <FormControl>
                  <Input type="number" placeholder="500" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="costPerStudentForeign"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cost/Student (Foreign)</FormLabel>
                <FormControl>
                  <Input type="number" placeholder="25" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Auto-generated credentials preview */}
        {slug && (
          <div className="rounded-xl border bg-gradient-to-br from-muted/50 to-muted p-4 space-y-2.5">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Auto-generated Credentials
            </p>
            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-primary/10 text-primary shrink-0">
                <Mail className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-muted-foreground leading-none mb-0.5">
                  Email
                </p>
                <p className="font-mono text-sm font-medium truncate">
                  {autoEmail}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-primary/10 text-primary shrink-0">
                <KeyRound className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-muted-foreground leading-none mb-0.5">
                  Password
                </p>
                <p className="font-mono text-sm font-medium">{autoPassword}</p>
              </div>
            </div>
          </div>
        )}

        {/* Mahallu List */}
        <div className="space-y-2">
          <FormLabel>Mahallu List</FormLabel>
          <Textarea
            placeholder="Paste mahallu names here (one per line)"
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            rows={4}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleBulkAdd}
            disabled={!bulkText.trim()}
          >
            Add Mahallus
          </Button>
          {mahalluList.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {mahalluList.map((name) => (
                <Badge
                  key={name}
                  variant="secondary"
                  className="flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full"
                >
                  {name}
                  <button
                    type="button"
                    onClick={() => removeMahallu(name)}
                    className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20 transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
          {mahalluList.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {mahalluList.length} mahallu(s) added
            </p>
          )}
        </div>

        <Button
          type="submit"
          className="w-full h-11 text-sm font-semibold"
          disabled={isPending}
        >
          {isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating...
            </>
          ) : (
            "Create Chapter"
          )}
        </Button>
      </form>
    </Form>
  );
}

CreateChapterForm.propTypes = {
  onSuccess: PropTypes.func,
};
